/**
 * iter_226 · Phase 22 — Admin "Manage Singles Store"
 * Visible card in Admin Dashboard → this page.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;

const AdminSinglesStorePage = () => {
  const [listings, setListings] = useState([]);
  const [rev, setRev] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [audit, setAudit] = useState([]);

  const loadAll = useCallback(async () => {
    setBusy(true);
    try {
      const [l, r, c, a] = await Promise.all([
        api.get(`/admin/singles/listings${statusFilter ? `?status=${statusFilter}` : ""}`),
        api.get("/admin/singles/revenue?days=30"),
        api.get("/admin/singles/config"),
        api.get("/admin/singles/audit?limit=50"),
      ]);
      setListings(l.data.items || []);
      setRev(r.data);
      setCfg(c.data.config);
      setAudit(a.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, [statusFilter]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const act = async (listing_id, action) => {
    if (!window.confirm(`${action} this listing?`)) return;
    try {
      await api.post(`/admin/singles/${listing_id}/${action}`, {});
      toast.success(action);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const saveCfg = async () => {
    setBusy(true);
    try {
      await api.post("/admin/singles/config", { updates: cfg });
      toast.success("Config saved");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-6xl mx-auto p-5 space-y-5" data-testid="admin-singles-store">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">📀 Manage Singles Store</p>
            <h1 className="text-2xl font-black mt-1">Phase 22 · Singles Marketplace</h1>
            <p className="text-xs text-gray-500">Kill switch: <code className="text-purple-300">singles.enabled</code> · listing moderation gate: <code className="text-purple-300">singles.moderation_required</code></p>
          </div>
          <Link to="/admin/dashboard" className="text-[11px] text-gray-400 hover:text-white underline">← Admin Dashboard</Link>
        </div>

        {rev && (
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="admin-singles-revenue">
            {["sales", "gross_usd", "platform_fee_usd", "artist_net_usd"].map((k) => (
              <div key={k} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <p className="text-[9px] uppercase tracking-wider text-gray-500 font-black">{k.replace(/_/g, " ")}</p>
                <p className="text-xl font-black text-white mt-0.5">
                  {typeof rev.totals[k] === "number" && k.endsWith("_usd") ? fmtUsd(rev.totals[k]) : rev.totals[k]}
                </p>
              </div>
            ))}
          </section>
        )}

        {cfg && (
          <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="admin-singles-config">
            <h2 className="text-xs font-black uppercase tracking-wider text-gray-300">Fees & Safety</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]">
              {["singles.enabled", "singles.moderation_required"].map((k) => (
                <label key={k} className="rounded border border-white/10 bg-black/40 p-2 flex items-center justify-between">
                  <span className="text-gray-400 font-mono">{k}</span>
                  <input type="checkbox" checked={!!cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: e.target.checked })}
                         data-testid={`admin-cfg-${k}`} />
                </label>
              ))}
              {["singles.fee_pct.free", "singles.fee_pct.premium_monthly", "singles.fee_pct.premium_yearly", "singles.fee_pct.founding_artist", "singles.fee_pct.studio_pro",
                "singles.min_price_usd", "singles.max_price_usd", "singles.recommended_price_usd",
                "singles.settlement_hold_days", "singles.preview_seconds", "singles.max_downloads_per_day", "singles.max_active_per_artist"].map((k) => (
                <label key={k} className="rounded border border-white/10 bg-black/40 p-2">
                  <p className="text-gray-500 font-mono text-[10px]">{k}</p>
                  <Input type="number" step="0.01" value={cfg[k]}
                         onChange={(e) => setCfg({ ...cfg, [k]: parseFloat(e.target.value) })}
                         data-testid={`admin-cfg-${k}`}
                         className="h-7 bg-black/40 border-white/10 text-white" />
                </label>
              ))}
            </div>
            <Button onClick={saveCfg} disabled={busy} className="bg-cyan-500 hover:bg-cyan-400 text-black font-black" data-testid="admin-singles-save-cfg">
              Save configuration
            </Button>
            <p className="text-[10px] text-amber-200 bg-amber-500/5 border border-amber-500/20 rounded p-2">
              Public activation requires setting <code>singles.enabled = true</code>. Do NOT flip until launch approval.
            </p>
          </section>
        )}

        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="admin-singles-listings">
          <div className="flex justify-between mb-2 flex-wrap gap-2">
            <h2 className="text-xs font-black uppercase tracking-wider text-gray-300">Listings</h2>
            <div className="flex gap-1">
              {["", "draft", "pending_review", "published", "paused", "removed"].map((s) => (
                <button key={s || "all"} onClick={() => setStatusFilter(s)}
                        data-testid={`admin-filter-${s || "all"}`}
                        className={`text-[10px] px-2 py-1 rounded ${statusFilter === s ? "bg-emerald-500 text-black" : "bg-white/10 text-gray-300"}`}>
                  {s || "all"}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="text-gray-500 uppercase">
                <tr className="border-b border-white/10">
                  <th className="text-left py-1">Title</th>
                  <th className="text-left">Artist</th>
                  <th className="text-right">Price</th>
                  <th className="text-right">Sales</th>
                  <th className="text-right">Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {listings.map((li) => (
                  <tr key={li.listing_id} className="border-b border-white/5">
                    <td className="py-1 truncate max-w-[220px]">{li.title}</td>
                    <td>{li.primary_artist_name || li.artist_id}</td>
                    <td className="text-right">{fmtUsd(li.price_usd)}</td>
                    <td className="text-right">{li.sales_count}</td>
                    <td className="text-right"><span className="text-cyan-200">{li.status}</span></td>
                    <td className="text-right space-x-1">
                      {li.status === "pending_review" && (
                        <button onClick={() => act(li.listing_id, "approve")} className="text-emerald-300 hover:underline" data-testid={`admin-approve-${li.listing_id}`}>approve</button>
                      )}
                      {["published", "pending_review"].includes(li.status) && (
                        <button onClick={() => act(li.listing_id, "pause")} className="text-amber-300 hover:underline" data-testid={`admin-pause-${li.listing_id}`}>pause</button>
                      )}
                      {li.status !== "removed" && (
                        <button onClick={() => act(li.listing_id, "remove")} className="text-rose-300 hover:underline" data-testid={`admin-remove-${li.listing_id}`}>remove</button>
                      )}
                    </td>
                  </tr>
                ))}
                {listings.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-gray-500">No listings.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="admin-singles-audit">
          <h2 className="text-xs font-black uppercase tracking-wider text-gray-300 mb-2">Admin audit tail</h2>
          <div className="text-[10px] font-mono space-y-0.5">
            {audit.map((a) => (
              <div key={a.audit_id} className="text-gray-300">
                <span className="text-gray-500">{a.at}</span> · <span className="text-cyan-200">{a.action}</span> · <span className="text-white">{a.target}</span> · <span className="text-gray-500">{a.admin_id}</span>
              </div>
            ))}
            {audit.length === 0 && <p className="text-gray-500">No actions yet.</p>}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminSinglesStorePage;
